/**
 * ============================================================================
 * WheelSpin.tsx - DAILY WHEEL SPIN COMPONENT
 * ============================================================================
 *
 * The daily prize wheel, built on the same Chakra components and colors as
 * the rest of the app (modal, buttons, stat tiles). Only the wheel itself
 * keeps hand-written CSS, because it is drawn from rotated wedges and runs
 * a transform animation that Chakra has no primitive for.
 *
 * ============================================================================
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalBody, ModalCloseButton,
  Box, HStack, VStack, Text, Button, Badge, Grid, Spinner,
} from '@chakra-ui/react';
import { Gift, Clock, Zap, Flame, ChevronRight } from 'lucide-react';
import { POKER_COLORS } from '../theme/designSystem';
import wheelSpinService, {
  WHEEL_PRIZES,
  SpinResult,
  SpinStatus,
  formatTimeUntilSpin,
} from '../services/wheelSpinService';
import './WheelSpin.css';

// ============================================================================
// TYPES
// ============================================================================

interface WheelSpinProps {
  userId: string;
  isOpen: boolean;
  onClose: () => void;
  onWin?: (amount: number, type: string) => void;
}

interface WheelSpinButtonProps {
  userId: string;
  onClick: () => void;
  compact?: boolean;
}

const CARD = '#0f1722';
const BORDER = 'rgba(148,163,184,0.18)';

// ============================================================================
// WHEEL SPIN MODAL
// ============================================================================

const WheelSpin: React.FC<WheelSpinProps> = ({
  userId,
  isOpen,
  onClose,
  onWin,
}) => {
  const [status, setStatus] = useState<SpinStatus | null>(null);
  const [isSpinning, setIsSpinning] = useState(false);
  const [result, setResult] = useState<SpinResult | null>(null);
  const [showCelebration, setShowCelebration] = useState(false);
  // Index of the segment the wheel LANDED on - drives the winner spotlight.
  const [winnerIndex, setWinnerIndex] = useState<number | null>(null);
  const wheelRef = useRef<HTMLDivElement>(null);

  // ── The spin is ONE continuous motion, driven frame by frame ──────────
  // Earlier versions stitched three mechanisms together (a pause, then a
  // constant-speed CSS loop, then a CSS transition to the prize) and every
  // seam showed as a jump. Now a single requestAnimationFrame loop owns the
  // angle: it accelerates the instant you tap, cruises while the server
  // picks the prize, and once the prize is known decelerates smoothly to a
  // stop exactly on the winning slice. React never re-renders per frame;
  // the transform is written straight to the element.
  const angleRef = useRef(0);          // current angle, degrees, grows forever
  const velocityRef = useRef(0);       // degrees per second
  const rafRef = useRef<number | null>(null);
  const lastFrameRef = useRef(0);
  const phaseRef = useRef<'idle' | 'accel' | 'cruise' | 'decel'>('idle');
  const decelRef = useRef<{ start: number; distance: number; duration: number; t0: number; onDone: () => void } | null>(null);
  const CRUISE_SPEED = 600;            // deg/s, about 1.7 turns a second
  const ACCEL_MS = 700;

  const paint = () => {
    const el = wheelRef.current;
    if (el) el.style.transform = `rotate(${angleRef.current}deg)`;
  };

  const stopLoop = () => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    phaseRef.current = 'idle';
    decelRef.current = null;
  };

  const frame = (now: number) => {
    const dt = Math.min(0.05, (now - lastFrameRef.current) / 1000); // clamp a stalled tab
    lastFrameRef.current = now;
    const phase = phaseRef.current;
    if (phase === 'accel') {
      // Ease in over ACCEL_MS, then hand over to a steady cruise
      const elapsed = now - (decelRef.current?.t0 ?? now);
      const k = Math.min(1, elapsed / ACCEL_MS);
      velocityRef.current = CRUISE_SPEED * (1 - Math.pow(1 - k, 2));
      angleRef.current += velocityRef.current * dt;
      if (k >= 1) phaseRef.current = 'cruise';
    } else if (phase === 'cruise') {
      angleRef.current += CRUISE_SPEED * dt;
    } else if (phase === 'decel' && decelRef.current) {
      // Ease-out cubic from the cruise speed to zero, covering exactly
      // `distance` so the pointer stops on the prize. Its initial slope is
      // 3 * distance / duration, which we set equal to the cruise speed, so
      // there is no change of speed at the moment the braking starts.
      const d = decelRef.current;
      const t = Math.min(1, (now - d.t0) / d.duration);
      angleRef.current = d.start + d.distance * (1 - Math.pow(1 - t, 3));
      if (t >= 1) {
        paint();
        const done = d.onDone;
        stopLoop();
        done();
        return;
      }
    } else {
      stopLoop();
      return;
    }
    paint();
    rafRef.current = requestAnimationFrame(frame);
  };

  const startLoop = () => {
    if (rafRef.current != null) return;
    lastFrameRef.current = performance.now();
    rafRef.current = requestAnimationFrame(frame);
  };

  const beginSpin = () => {
    phaseRef.current = 'accel';
    velocityRef.current = 0;
    decelRef.current = { start: angleRef.current, distance: 0, duration: 0, t0: performance.now(), onDone: () => undefined };
    startLoop();
  };

  /** Brake to a stop with the pointer on `targetMod` (0-360), then call onDone. */
  const brakeTo = (targetMod: number, onDone: () => void) => {
    const reduced = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const current = angleRef.current;
    const currentMod = ((current % 360) + 360) % 360;
    let distance = (targetMod - currentMod + 360) % 360;
    // Enough extra turns that the braking lasts about 3.5 to 5 seconds at
    // the cruise speed (ease-out cubic: duration = 3 * distance / speed).
    const minDistance = reduced ? 360 : 720 + Math.floor(Math.random() * 2) * 360;
    while (distance < minDistance) distance += 360;
    const speed = Math.max(velocityRef.current, CRUISE_SPEED * 0.6);
    const duration = reduced ? 900 : (3 * distance / speed) * 1000;
    decelRef.current = { start: current, distance, duration, t0: performance.now(), onDone };
    phaseRef.current = 'decel';
    startLoop();
  };

  // Stop the loop if the modal closes or the component goes away mid-spin
  useEffect(() => () => stopLoop(), []);

  // Load spin status on open
  useEffect(() => {
    if (isOpen && userId) {
      setResult(null);
      setWinnerIndex(null);
      setShowCelebration(false);
      // The wheel element is fresh each time the modal opens: start it at
      // rest at zero so the first frame of a spin cannot jump to an old angle.
      stopLoop();
      angleRef.current = 0;
      velocityRef.current = 0;
      paint();
      loadStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, userId]);

  const loadStatus = async () => {
    const spinStatus = await wheelSpinService.getSpinStatus(userId);
    setStatus(spinStatus);
  };

  // Handle spin
  const handleSpin = useCallback(async () => {
    if (isSpinning || !status?.canSpin) return;

    setIsSpinning(true);
    setResult(null);
    setShowCelebration(false);
    setWinnerIndex(null);

    // Start moving on the tap itself. The prize comes from a callable and a
    // cold start can take seconds; the wheel simply keeps cruising until it
    // arrives, with no visible change of speed.
    const spinStartedAt = performance.now();
    beginSpin();

    // Get result from the SERVER (spinDailyWheel callable): it picks the
    // prize, enforces the once-per-day limit, tracks the streak and credits
    // the wallet in one place.
    const spinResult = await wheelSpinService.spinWheel(userId);

    // Let it cruise for at least a moment so a fast server still reads as a
    // real spin rather than a twitch.
    const minCruiseMs = ACCEL_MS + 500;
    const waited = performance.now() - spinStartedAt;
    if (waited < minCruiseMs) await new Promise(r => setTimeout(r, minCruiseMs - waited));

    if (spinResult.success && spinResult.prize) {
      // GEOMETRY: .wheel-segment is a right-triangle clip anchored at the
      // centre spanning EAST -> SOUTH-EAST before its rotate(i*seg), so
      // segment i covers [i*seg, (i+1)*seg] measured clockwise from EAST
      // (3 o'clock) - while the pointer sits at the TOP, i.e. 270 degrees.
      const prizeIndex = wheelSpinService.getPrizeIndex(spinResult.prize.id);
      const segAngle = 360 / WHEEL_PRIZES.length;
      const prizeAngle = prizeIndex * segAngle;
      // Aim just off dead centre of the slice so it never looks machine-placed
      const jitter = (Math.random() - 0.5) * segAngle * 0.5;
      const targetMod = (((270 - prizeAngle - segAngle / 2 + jitter) % 360) + 360) % 360;

      brakeTo(targetMod, () => {
        setResult(spinResult);
        setIsSpinning(false);
        setShowCelebration(true);
        setWinnerIndex(prizeIndex);
        if (onWin && spinResult.prize) {
          onWin(spinResult.finalValue, spinResult.prize.type);
        }
        loadStatus();
        setTimeout(() => setShowCelebration(false), 3000);
      });
    } else {
      // Rejected (already spun today, or an error): brake gently to wherever
      // it lands instead of freezing mid-flight with no explanation.
      brakeTo(((angleRef.current % 360) + 360) % 360, () => {
        setResult(spinResult);
        setIsSpinning(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSpinning, status, userId, onWin]);

  const segmentAngle = 360 / WHEEL_PRIZES.length;
  const won = result?.success ? result : null;
  const prizeLabel = won?.prize
    ? won.prize.type === 'pkcash' ? `${won.finalValue} PSS Coins`
      : won.prize.type === 'jackpot' ? `${won.finalValue.toLocaleString()} PlayCoins top prize`
      : `${won.finalValue.toLocaleString()} PlayCoins`
    : '';

  return (
    <Modal isOpen={isOpen} onClose={isSpinning ? () => undefined : onClose} isCentered size={{ base: 'full', sm: 'lg' }} motionPreset="slideInBottom">
      <ModalOverlay bg="blackAlpha.800" backdropFilter="blur(4px)" />
      <ModalContent bg="#0a1420" color="white" border="1px solid" borderColor={`${POKER_COLORS.gold}55`} borderRadius={{ base: 0, sm: '2xl' }}
        boxShadow={`0 0 60px ${POKER_COLORS.gold}22`} overflow="hidden" maxH={{ base: '100vh', sm: '94vh' }}>
        <ModalCloseButton mt={1} isDisabled={isSpinning} />
        <ModalBody px={{ base: 4, md: 6 }} pt={5} pb={6} overflowY="auto">
          <VStack spacing={4} align="stretch">
            {/* Header */}
            <HStack spacing={3}>
              <Box bg={`${POKER_COLORS.gold}22`} color={POKER_COLORS.gold} p={3} borderRadius="xl"><Gift size={24} /></Box>
              <Box flex={1}>
                <Text fontSize="xl" fontWeight="900" lineHeight="1.15">Daily Spin</Text>
                <Text color="gray.400" fontSize="sm">One free spin every day. Prizes go straight to your wallet.</Text>
              </Box>
              {status && status.currentStreak > 0 && (
                <Badge bg={`${POKER_COLORS.gold}22`} color={POKER_COLORS.gold} borderRadius="full" px={3} py={1} fontSize="sm" textTransform="none" display="flex" alignItems="center" gap={1}>
                  <Flame size={14} /> {status.currentStreak} day streak
                </Badge>
              )}
            </HStack>

            {/* Wheel */}
            <Box className="wheel-container" position="relative">
              <div className="wheel-pointer" />
              <div
                ref={wheelRef}
                className={`wheel ${isSpinning ? 'spinning' : ''}`}
              >
                {WHEEL_PRIZES.map((prize, index) => (
                  <div
                    key={prize.id}
                    className="wheel-segment"
                    style={{
                      transform: `rotate(${index * segmentAngle}deg)`,
                      backgroundColor: prize.color,
                      // The wedge angle is DERIVED from the segment count so
                      // the colours never overlap however many prizes there are.
                      clipPath: `polygon(0 0, 100% 0, 100% ${Math.tan((segmentAngle * Math.PI) / 180) * 100}%)`,
                    }}
                  />
                ))}
                {WHEEL_PRIZES.map((_, index) => (
                  <div key={`spoke-${index}`} className="wheel-spoke" style={{ transform: `rotate(${index * segmentAngle + 90}deg)` }} />
                ))}
                {WHEEL_PRIZES.map((prize, index) => {
                  // Labels are full-size layers rotated to the wedge's mid-line
                  // (+90 because text starts at 12 o'clock, wedges at 3).
                  const labelRot = index * segmentAngle + segmentAngle / 2 + 90;
                  const norm = ((labelRot % 360) + 360) % 360;
                  const flip = norm > 90 && norm < 270;
                  return (
                    <div key={`label-${prize.id}`} className="wheel-label" style={{ transform: `rotate(${labelRot}deg)` }}>
                      <span
                        className={`wheel-label-text${prize.type === 'pkcash' ? ' cash' : ''}${winnerIndex === index ? ' winner' : ''}`}
                        style={flip ? { transform: 'rotate(180deg)' } : undefined}
                      >
                        {prize.label}
                      </span>
                    </div>
                  );
                })}
                {/* Landed: every losing slice dims so the winner is unmistakable */}
                {winnerIndex != null && WHEEL_PRIZES.map((prize, index) => (
                  index !== winnerIndex ? (
                    <div
                      key={`dim-${prize.id}`}
                      className="wheel-segment wheel-dim"
                      style={{
                        transform: `rotate(${index * segmentAngle}deg)`,
                        clipPath: `polygon(0 0, 100% 0, 100% ${Math.tan((segmentAngle * Math.PI) / 180) * 100}%)`,
                      }}
                    />
                  ) : null
                ))}
                <div className="wheel-center"><Gift size={28} /></div>
              </div>
            </Box>

            {/* Action / result */}
            {won ? (
              <Box bg="rgba(34,197,94,0.12)" border="1px solid rgba(34,197,94,0.5)" borderRadius="2xl" p={4} textAlign="center"
                className={showCelebration ? 'wheel-result-pop' : undefined}>
                <Text color="#4ade80" fontSize="sm" fontWeight="800" textTransform="uppercase" letterSpacing="0.06em">You won</Text>
                <Text color="white" fontSize="2xl" fontWeight="900" lineHeight="1.15" mt={1}>{prizeLabel}</Text>
                {won.streakBonus > 0 && won.prize && (
                  <Text color={POKER_COLORS.gold} fontSize="sm" mt={1}>
                    {won.prize.value.toLocaleString()} on the wheel + {Math.round(won.streakBonus * 100)}% streak bonus
                  </Text>
                )}
                <Text color="gray.400" fontSize="sm" mt={2}>Added to your wallet. Come back tomorrow for another spin.</Text>
                <Button mt={3} minH="48px" w="100%" bg={POKER_COLORS.cyan} color="black" _hover={{ bg: '#22e5e5' }} fontSize="md" fontWeight="800" onClick={onClose}>
                  Done
                </Button>
              </Box>
            ) : result && !result.success ? (
              <Box bg={CARD} border="1px solid" borderColor={BORDER} borderRadius="2xl" p={4} textAlign="center">
                <HStack justify="center" color="gray.300" spacing={2}><Clock size={18} /><Text fontWeight="700">{result.message}</Text></HStack>
                {result.nextSpinTime && <Text color={POKER_COLORS.gold} fontSize="sm" mt={1}>Next spin in {formatTimeUntilSpin(result.nextSpinTime)}</Text>}
              </Box>
            ) : status?.canSpin ? (
              <Button minH="60px" w="100%" fontSize="xl" fontWeight="900" color="#1a1206"
                bg={`linear-gradient(135deg, ${POKER_COLORS.gold} 0%, #f59e0b 100%)`} _hover={{ transform: 'translateY(-1px)', boxShadow: `0 8px 30px ${POKER_COLORS.gold}66` }}
                _active={{ transform: 'scale(0.99)' }} boxShadow={`0 6px 24px ${POKER_COLORS.gold}44`}
                leftIcon={isSpinning ? <Spinner size="sm" /> : <Zap size={22} />} onClick={handleSpin} isDisabled={isSpinning}>
                {isSpinning ? 'Spinning' : 'Spin'}
              </Button>
            ) : status ? (
              <Box bg={CARD} border="1px solid" borderColor={BORDER} borderRadius="2xl" p={4} textAlign="center">
                <HStack justify="center" color="gray.300" spacing={2}><Clock size={18} /><Text fontWeight="700">You have spun today</Text></HStack>
                {status.nextSpinTime && <Text color={POKER_COLORS.gold} fontSize="sm" mt={1}>Next spin in {formatTimeUntilSpin(status.nextSpinTime)}</Text>}
              </Box>
            ) : (
              <HStack justify="center" minH="60px"><Spinner color={POKER_COLORS.cyan} /></HStack>
            )}

            {/* Stats */}
            {status && (
              <Grid templateColumns="repeat(3, 1fr)" gap={3}>
                {[
                  ['Spins', status.totalSpins.toLocaleString()],
                  ['Won', status.totalWon.toLocaleString()],
                  ['Streak', `${status.currentStreak} day${status.currentStreak === 1 ? '' : 's'}`],
                ].map(([label, value]) => (
                  <Box key={label} bg={CARD} border="1px solid" borderColor={BORDER} borderRadius="xl" p={3} textAlign="center">
                    <Text color="white" fontWeight="900" fontSize="lg" lineHeight={1.1}>{value}</Text>
                    <Text color="gray.500" fontSize="xs" mt={1} textTransform="uppercase" letterSpacing="0.06em">{label}</Text>
                  </Box>
                ))}
              </Grid>
            )}
            <Text color="gray.500" fontSize="xs" textAlign="center">
              Spin on consecutive days and your streak bonus grows. The result is decided on the server and what you see land is what is paid.
            </Text>
          </VStack>

          {/* Celebration confetti */}
          {showCelebration && (
            <div className="wheel-confetti">
              {[...Array(50)].map((_, i) => (
                <div
                  key={i}
                  className="confetti-piece"
                  style={{
                    left: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 0.5}s`,
                    backgroundColor: WHEEL_PRIZES[i % WHEEL_PRIZES.length].color,
                  }}
                />
              ))}
            </div>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

// ============================================================================
// WHEEL SPIN BUTTON (For Homepage/Nav)
// ============================================================================

export const WheelSpinButton: React.FC<WheelSpinButtonProps> = ({
  userId,
  onClick,
  compact = false,
}) => {
  const [canSpin, setCanSpin] = useState(false);
  const [nextSpinTime, setNextSpinTime] = useState<number | null>(null);

  useEffect(() => {
    if (userId) {
      checkStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const checkStatus = async () => {
    const status = await wheelSpinService.getSpinStatus(userId);
    setCanSpin(status.canSpin);
    setNextSpinTime(status.nextSpinTime);
  };

  if (compact) {
    return (
      <Button aria-label="Daily Spin" onClick={onClick} w="48px" h="48px" minW="48px" p={0} borderRadius="full"
        bg={canSpin ? `${POKER_COLORS.gold}22` : 'whiteAlpha.100'} color={canSpin ? POKER_COLORS.gold : 'gray.400'}
        border="1px solid" borderColor={canSpin ? POKER_COLORS.gold : 'whiteAlpha.300'} position="relative"
        className={canSpin ? 'wheel-available-pulse' : undefined} title={canSpin ? 'Spin the wheel' : 'Come back tomorrow'}>
        <Gift size={22} />
        {canSpin && <Box position="absolute" top="-3px" right="-3px" w="12px" h="12px" borderRadius="full" bg="#ef4444" border="2px solid #0a1420" />}
      </Button>
    );
  }

  return (
    <Box as="button" onClick={onClick} w="100%" textAlign="left"
      bg={canSpin ? `linear-gradient(135deg, ${POKER_COLORS.gold}1f 0%, #f59e0b14 100%)` : CARD}
      border="1px solid" borderColor={canSpin ? POKER_COLORS.gold : BORDER} borderRadius="2xl" p={4}
      boxShadow={canSpin ? `0 0 24px ${POKER_COLORS.gold}33` : undefined}
      _hover={{ borderColor: POKER_COLORS.gold, transform: 'translateY(-1px)' }} _active={{ transform: 'scale(0.995)' }} transition="all 0.15s">
      <HStack spacing={4}>
        <Box bg={canSpin ? POKER_COLORS.gold : 'whiteAlpha.100'} color={canSpin ? '#1a1206' : 'gray.400'} p={3} borderRadius="xl" flexShrink={0}
          className={canSpin ? 'wheel-available-pulse' : undefined}>
          <Gift size={24} />
        </Box>
        <Box flex={1} minW={0}>
          <HStack spacing={2}>
            <Text color="white" fontWeight="800" fontSize="lg" lineHeight="1.2">Daily Spin</Text>
            {canSpin && <Badge bg={POKER_COLORS.gold} color="#1a1206" borderRadius="full" px={2} fontSize="10px">Free</Badge>}
          </HStack>
          <Text color={canSpin ? POKER_COLORS.gold : 'gray.400'} fontSize="md" mt={0.5}>
            {canSpin ? 'Your free spin is ready' : nextSpinTime ? `Next spin in ${formatTimeUntilSpin(nextSpinTime)}` : 'Come back tomorrow'}
          </Text>
        </Box>
        <Box color={canSpin ? POKER_COLORS.gold : 'gray.500'} flexShrink={0}>{canSpin ? <Zap size={22} /> : <ChevronRight size={22} />}</Box>
      </HStack>
    </Box>
  );
};

export default WheelSpin;
